import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import queryString from 'query-string';

import { SCREEN_ID_FEED } from 'client/constants/screenIds';
import postWithState from 'client/actions/postWithState';

import {
  SR_API_ACTION_RENTLINX_LEAD_EMAIL_DRIP_OPT_OUT,
} from 'api/actionTypes';

import {
  DEFAULT_MARKET_ID,
} from 'common/marketConstants';
import {
  FEED_CATEGORY_QUALIFIED_TO_RENT,
  FEED_CATEGORY_BEST_MATCHES,
  FEED_CATEGORY_SELF_TOUR,
  FEED_CATEGORY_MOST_POPULAR,
  FEED_CATEGORY_FURTHER_COMMUTE,
  FEED_CATEGORY_STAY_IN_YOUR_BUDGET,
  FEED_CATEGORY_STAY_IN_YOUR_LOCATION,
  FEED_CATEGORY_EXCLUSIVE_APARTMENTS,
  FEED_CATEGORY_SUNROOM_LISTINGS,
  FEED_CATEGORY_LAST_VISITED,
  FEED_CATEGORY_LAST_VISITED_ANONYMOUS,
} from 'common/feedCategoryConstants';
import {
  actionInitializeFeedCategoryList,
  actionFeedCategoryLoadlistings,
  shouldFeedCategoryLoadInitialListings,
} from 'client/helpers/feedCategoryHelpers';
import {
  getCurrentUserRenterProfile,
} from 'common/householdHelpers';
import {
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';
import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_FEED_CHANGE_FILTERS_CLICK,
  ANALYTICS_EVENT_MAP_SEARCHED,
} from 'client/analytics/eventList';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import NavbarButton from 'client/components/NavbarButton';
import SrButtonDeprecated from 'client/components/SrButtonDeprecated';
import SrFeedCategory from 'client/components/SrFeedCategory';
import TabBar from 'client/components/TabBar';
import SrPage from 'client/components/SrPage';
import SrBrowseBestListingsModal from 'client/components/SrBrowseBestListingsModal/SrBrowseBestListingsModal';

import ListingActions from 'client/actions/Listing';
import UserActions from 'client/actions/User';

import branch from 'client/core/branch';

@withTransitionHook
@withScreenId(SCREEN_ID_FEED)
@withScrollToTop
@branch({
  currentUser: ['currentUser'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  qualifiedCategoryFeedViewed: [
    'currentUser',
    'settings',
    'qualifiedCategoryFeedViewed',
  ],
  currentUserMarket: ['markets', 'currentUserMarket'],
})
@withRouter
class SrFeedPage extends Component {
  static fetchData(match, inject) {
    actionInitializeFeedCategoryList(inject);

    const renterProfile = getCurrentUserRenterProfile({ state: inject.state });
    const fetchDataPromises = [];

    if (!inject.state.get(['listings', 'totalListingsCount'])) {
      fetchDataPromises.push(ListingActions.getTotalListingsCount(inject));
    }

    if (
      renterProfile
      && renterProfile.isPrequalCompleted
      && shouldFeedCategoryLoadInitialListings(inject, { feedCategoryId: FEED_CATEGORY_QUALIFIED_TO_RENT })
    ) {
      fetchDataPromises.push(
        actionFeedCategoryLoadlistings(inject, { feedCategoryId: FEED_CATEGORY_QUALIFIED_TO_RENT })
      );
    }

    if (shouldFeedCategoryLoadInitialListings(inject, { feedCategoryId: FEED_CATEGORY_BEST_MATCHES })) {
      fetchDataPromises.push(
        actionFeedCategoryLoadlistings(inject, { feedCategoryId: FEED_CATEGORY_BEST_MATCHES })
      );
    }

    if (fetchDataPromises.length) {
      return Promise.all(fetchDataPromises);
    }
  }

  static getPageMetadata = () => ({
    pageTitle: 'Rent the best homes in Texas | Sunroom Rentals',
    section: 'Listings',
    keywords: 'rent, home, austin, tour, apartment',
    description: (
      'Spend less time scheduling tours, our rental experts will schedule ' +
      'and confirm tours for you.'
    ),
    image: 'https://sunroomrentals.com/images/metadata/og-house.jpg',
  })

  static propTypes = {
    qualifiedCategoryFeedViewed: PropTypes.bool,
    currentUser: PropTypes.object.isRequired,
    renterProfilesByUserId: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    currentUserMarket: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      showBrowseBestListingsModal: false,
    };
  }

  componentDidMount() {
    const { dispatch, history, location, match } = this.props;
    const locationQuery = queryString.parse(location.search);
    const rentlinxListingLeadIdDripOptOut = isNaN(locationQuery.rentlinxListingLeadIdOptOut)
      ? null
      : parseInt(locationQuery.rentlinxListingLeadIdOptOut, 10);

    if (rentlinxListingLeadIdDripOptOut) {
      this.setState({
        showBrowseBestListingsModal: true,
      });
      history.replace(match.url);
      dispatch(postWithState, {
        action: SR_API_ACTION_RENTLINX_LEAD_EMAIL_DRIP_OPT_OUT,
        payload: {
          rentlinxListingLeadId: rentlinxListingLeadIdDripOptOut,
        },
      });
    }
  }

  _onChangeFiltersButtonClick = () => {
    const {
      history,
    } = this.props;

    analytics.track(ANALYTICS_EVENT_FEED_CHANGE_FILTERS_CLICK);
    history.push('/search');
  }

  _closeBrowseBestListingsModal = () => {
    this.setState({ showBrowseBestListingsModal: false });
  }

  _onViewedQualifedCategory = () => {
    const { dispatch } = this.props;
    const { currentUser } = this.props;
    if (!currentUser.settings.qualifiedCategoryFeedViewed) {
      setTimeout(() => {
        dispatch(UserActions.updateSettings, {
          qualifiedCategoryFeedViewed: true,
        });
      }, 5000);
    }
  }

  _onMapButtonClick = () => {
    const { history } = this.props;
    analytics.track(ANALYTICS_EVENT_MAP_SEARCHED);
    history.push('/explore/map');
  }

  render() {
    const {
      history,
      currentUser,
      qualifiedCategoryFeedViewed,
      currentUserMarket,
    } = this.props;

    const {
      showBrowseBestListingsModal,
    } = this.state;

    const renterProfile = getCurrentUserRenterProfile(this.props);

    const feedCategoriesDisabled = (currentUserMarket.settings.feedCategories || {}).disabled || [];

    return (
      <SrPage
        navbarProps={{
          leftButton: (
            <NavbarButton
              onClick={() => history.push('/search')}
            >
              Filter
            </NavbarButton>
          ),
          rightButton: (
            <NavbarButton
              onClick={this._onMapButtonClick}
            >
              Map
            </NavbarButton>
          ),
        }}
      >
        <div className='flex-center'>
          <div className='SrPageContainer SrFeedContainer'>
            <div className='SrFeedCategoryContainer'>
              <SrFeedCategory
                feedCategoryId={currentUser.roles.includes(USER_ROLE_ANONYMOUS)
                  ? FEED_CATEGORY_LAST_VISITED_ANONYMOUS
                  : FEED_CATEGORY_LAST_VISITED
                }
                currentUser={currentUser}
                market={currentUserMarket}
                history={history}
              />
              <SrFeedCategory
                feedCategoryId={FEED_CATEGORY_BEST_MATCHES}
                currentUser={currentUser}
                market={currentUserMarket}
                history={history}
              />
              {currentUserMarket.id === DEFAULT_MARKET_ID && (
                <SrFeedCategory
                  feedCategoryId={FEED_CATEGORY_SELF_TOUR}
                  currentUser={currentUser}
                  market={currentUserMarket}
                  history={history}
                />
              )}
              {
                renterProfile
                && renterProfile.isPrequalCompleted
                && !feedCategoriesDisabled.includes(FEED_CATEGORY_QUALIFIED_TO_RENT)
                && (
                  <SrFeedCategory
                    feedCategoryId={FEED_CATEGORY_QUALIFIED_TO_RENT}
                    currentUser={currentUser}
                    market={currentUserMarket}
                    history={history}
                    onViewedQualifedCategory={this._onViewedQualifedCategory}
                    highlight={!qualifiedCategoryFeedViewed}
                  />
                )
              }
              <SrFeedCategory
                feedCategoryId={FEED_CATEGORY_SUNROOM_LISTINGS}
                currentUser={currentUser}
                market={currentUserMarket}
                history={history}
              />
              <SrFeedCategory
                feedCategoryId={FEED_CATEGORY_STAY_IN_YOUR_BUDGET}
                currentUser={currentUser}
                market={currentUserMarket}
                history={history}
              />
              <SrFeedCategory
                feedCategoryId={FEED_CATEGORY_STAY_IN_YOUR_LOCATION}
                market={currentUserMarket}
                currentUser={currentUser}
                history={history}
              />
              {!feedCategoriesDisabled.includes(FEED_CATEGORY_MOST_POPULAR) && (
                <SrFeedCategory
                  feedCategoryId={FEED_CATEGORY_MOST_POPULAR}
                  currentUser={currentUser}
                  market={currentUserMarket}
                  history={history}
                />
              )}
              {currentUserMarket.id === DEFAULT_MARKET_ID && (
                <SrFeedCategory
                  feedCategoryId={FEED_CATEGORY_EXCLUSIVE_APARTMENTS}
                  currentUser={currentUser}
                  market={currentUserMarket}
                  history={history}
                />
              )}
              <SrFeedCategory
                feedCategoryId={FEED_CATEGORY_FURTHER_COMMUTE}
                currentUser={currentUser}
                market={currentUserMarket}
                history={history}
              />
            </div>
            <div className='flex-center'>
              <SrButtonDeprecated
                text='Change your filters'
                onClick={this._onChangeFiltersButtonClick}
                leftIconClassName='gear'
                className='yellow'
              />
            </div>
            {showBrowseBestListingsModal && (
              <SrBrowseBestListingsModal
                onClose={this._closeBrowseBestListingsModal}
                headline={'We\'ve stopped sending you emails about that property.'}
              />
            )}
          </div>
        </div>
        <TabBar />
      </SrPage>
    );
  }
}

SrFeedPage.displayName = 'SrFeedPage';

export default SrFeedPage;
